import type { ReactNode } from 'react';
import { cloneElement, isValidElement, useState } from 'react';

import { useTranslations } from 'utils/translations/useTranslations';

import './Navbar.scss';

type NavbarProps = { children: ReactNode };

type ChildProps = {
  // biome-ignore lint/suspicious/noExplicitAny: onClick can have any args
  onClick?: (...args: any[]) => void;
};

const Navbar = ({ children }: NavbarProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const i18n = useTranslations();

  function handleButtonClick() {
    setIsOpen(isOpen => !isOpen);
    document.body.classList.toggle('Navbar-open');
  }

  function handleCloseClick() {
    document.body.classList.remove('Navbar-open');
  }

  function handleChildClick(
    child: React.ReactElement<ChildProps>,
    args: unknown[],
  ) {
    if (child.props.onClick && typeof child.props.onClick === 'function') {
      child.props.onClick(...args);
    }

    handleCloseClick();
  }

  const renderMenuItem = (child: ReactNode, index: number) => {
    if (isValidElement<ChildProps>(child)) {
      return (
        <li className="Navbar-menu-item" key={index}>
          {cloneElement(child, {
            onClick: (...args: unknown[]) => handleChildClick(child, args),
          })}
        </li>
      );
    }

    return null;
  };

  const menuItems = Array.isArray(children)
    ? children.map(renderMenuItem)
    : children;
  return (
    <nav className="Navbar" aria-label={i18n.t('navbar.label') as string}>
      <section className="Navbar-header">
        <h3 className="Navbar-title">{i18n.t('navbar.menu')}</h3>
        <button
          data-testid="OpenMobileMenu"
          className="Navbar-button"
          onClick={handleButtonClick}
          aria-labelledby="navbar-button-text"
          aria-expanded={isOpen}
          type="button"
        >
          <span className="Navbar-button-icon" />
          <span id="navbar-button-text" className="Navbar-button-text">
            {isOpen ? i18n.t('navbar.close') : i18n.t('navbar.menu')}
          </span>
        </button>
      </section>

      <ul className="Navbar-menu">{menuItems}</ul>
    </nav>
  );
};

export { Navbar };
